import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { Input, Textarea } from "@material-tailwind/react";
import useTaskDetails from "@/features/task/hooks/useTaskDetails";
import FormHeader from "@/features/task/components/taskForm/FormHeader";
import PrioritySelector from "@/features/task/components/taskForm/PrioritySelector";
import ProjectSelector from "@/features/task/components/taskForm/ProjectSelector";
import ClientSelector from "@/features/task/components/taskForm/ClientSelector";
import ChargesSection from "@/features/task/components/taskForm/ChargesSection";
import MissionsSection from "@/features/task/components/taskForm/MissionsSection";
import AssigneesSection from "@/features/task/components/taskForm/AssigneesSection";
import FormActions from "@/features/task/components/taskForm/FormActions";
import useAssignments from "@/features/assignments/hooks/useAssignments";
import useCities from "@/features/city/hooks/useCities";
import CitySelector from "@/features/task/components/taskForm/CitySelector";
import TravelsSection from "@/features/task/components/taskForm/TravelSection";
import { toast } from "react-toastify";
import ParentTaskSelector from "@/features/task/components/taskForm/ParentTaskSelector";
import useParentTasks from "@/features/task/hooks/useParentTasks";
const TaskForm = ({ taskId, onSubmit, onCancel, initialParentTask }) => {
    const { task, loading, error } = useTaskDetails(Number(taskId));
    const { assignees } = useAssignments(undefined, taskId);
    const { tasks: parentTasks } = useParentTasks();
    const [formFields, setFormFields] = useState({
        title: "",
        description: "",
        adresse: "",
        ref_fonciere: "",
        due_date: ""
    });
    const [priority, setPriority] = useState(undefined);
    const [project, setProject] = useState(undefined);
    const [client, setClient] = useState(undefined);
    const [taskCity, setTaskCity] = useState(undefined);
    const [taskMissions, setTaskMissions] = useState([]);
    const [taskCharges, setTaskCharges] = useState([]);
    const [taskTravels, setTaskTravels] = useState([]);
    const [taskAssignees, setTaskAssignees] = useState([]);
    const [parentTask, setParentTask] = useState(initialParentTask ?? undefined);
    const { cities } = useCities(undefined, undefined, true);
    const previousProjectKeyRef = useRef(undefined);
    const [errors, setErrors] = useState({});
    useEffect(() => {
        if (task) {
            setFormFields((prev) => ({
                ...prev,
                title: task.title,
                description: task.description ?? "",
                ref_fonciere: task.ref_fonciere ?? "",
                adresse: task.adresse ?? "",
                due_date: task.due_date ?? "",
            }));
            if (task.ville_id) {
                const initCity = cities.find((c) => c.id === task.ville_id);
                setTaskCity(initCity);
            }
            setTaskMissions(task.taskMissions || []);
            setTaskCharges(task.taskCharges || []);
            setTaskAssignees(assignees || []);
            setPriority(task.priority || undefined);
            setProject(task.project || undefined);
            if (task.project) {
                previousProjectKeyRef.current =
                    task.project.id !== undefined
                        ? `id:${task.project.id}`
                        : task.project.label
                            ? `label:${task.project.label}`
                            : undefined;
            }
            setClient(task.client || undefined);
            if (task.parent_id) {
                const initParent = parentTasks.find((t) => t.id === task.parent_id);
                if (initParent) {
                    setParentTask(initParent);
                }
            }
        }
        else if (initialParentTask) {
            setParentTask(initialParentTask);
        }
    }, [assignees, cities, task, parentTasks, initialParentTask]);
    useEffect(() => {
        if (!project) {
            previousProjectKeyRef.current = undefined;
            setTaskCity(undefined);
            setClient(undefined);
            return;
        }
        const projectKey = project.id !== undefined ? `id:${project.id}` : `label:${project.label ?? ""}`;
        const projectChanged = previousProjectKeyRef.current !== projectKey;
        const shouldUpdateCity = projectChanged || (!taskCity && Boolean(project.ville_id || project.ville));
        const shouldUpdateClient = projectChanged || !client;
        if (projectChanged) {
            previousProjectKeyRef.current = projectKey;
        }
        if (shouldUpdateCity) {
            if (project.ville_id) {
                const projCity = cities.find((c) => c.id === project.ville_id);
                if (projCity) {
                    setTaskCity(projCity);
                }
                else if (project.ville) {
                    setTaskCity({
                        id: project.ville_id,
                        label: project.ville
                    });
                }
            }
            else if (project.ville) {
                setTaskCity({
                    id: project.ville_id,
                    label: project.ville
                });
            }
        }
        if (shouldUpdateClient) {
            if (project.client) {
                setClient(project.client);
            }
            else if (project.client_id || project.client_denomination) {
                setClient({
                    id: project.client_id,
                    denomination: project.client_denomination ?? ""
                });
            }
            else {
                setClient(undefined);
            }
        }
    }, [project, cities, taskCity, client]);
    const validateFields = () => {
        const newErrors = {};
        if (!formFields.title.trim()) {
            newErrors.title = "Le résumé est obligatoire.";
        }
        else if (formFields.title.length < 3) {
            newErrors.title = "Le résumé doit contenir au moins 3 caractères.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSave = async () => {
        if (!validateFields()) {
            return;
        }
        if (task && parentTask && task.id === parentTask.id) {
            setErrors((prev) => ({ ...prev, parentTask: "Une tâche ne peut pas être parent d'elle même." }));
            toast.error("Une tâche ne peut pas être parent d'elle même.");
            return;
        }
        const baseTask = {
            id: 0,
            title: formFields.title,
            priority: priority,
            project: project,
            project_id: project?.id,
            client_id: client?.id,
            client_label: client?.denomination,
            description: formFields.description,
            ref_fonciere: formFields.ref_fonciere,
            adresse: formFields.adresse,
            due_date: formFields.due_date || undefined,
            ville_id: taskCity?.id,
            taskMissions: taskMissions,
            taskCharges: taskCharges,
            travels: taskTravels,
            created_by: 1,
            assignees: taskAssignees,
            parent_id: parentTask?.id ?? null,
        };
        if (task && task.id && task.id !== 0) {
            const updatedTask = { ...task, ...baseTask };
            try {
                onSubmit(updatedTask);
                toast.success("Modifications enregistrées");
            }
            catch (error) {
                console.error("Erreur lors de la sauvegarde de la tâche :", error);
                toast.error(`Erreur : ${error.message}`);
            }
        }
        else {
            try {
                onSubmit(baseTask);
                toast.success("Tâche enregistré");
            }
            catch (error) {
                console.error("Erreur lors de la création de la tâche :", error);
                toast.error(`Erreur : ${error.message}`);
            }
        }
    };
    if (loading) {
        return _jsx("div", { className: "p-4", children: "Chargement..." });
    }
    if (error) {
        return _jsxs("div", { className: "p-4 text-red-500", children: ["Erreur : ", error] });
    }
    return (_jsxs("div", { className: "relative min-h-[70vh] sm:min-h-screen bg-white w-full", children: [_jsx(FormHeader, { task: task ?? undefined, taskId: taskId, onClose: onCancel }), _jsx("div", { className: "", children: _jsxs("form", { className: "space-y-6 max-h-[calc(100vh-110px)] overflow-y-auto p-4 pb-32 md:pb-6", children: [_jsx(ParentTaskSelector, { initialValue: parentTask, onSelect: (selectedParent) => {
                                if (task && selectedParent && selectedParent.id === task.id) {
                                    setErrors((prev) => ({ ...prev, parentTask: "Une tâche ne peut pas être parent d'elle même." }));
                                    toast.error("Une tâche ne peut pas être parent d'elle même.");
                                    return;
                                }
                                setErrors((prev) => ({ ...prev, parentTask: undefined }));
                                setParentTask(selectedParent);
                            }, currentTaskId: task?.id }), errors.parentTask && (_jsx("p", { className: "text-sm text-red-500 mt-1", children: errors.parentTask })), _jsx("div", { className: "flex items-center gap-2", children: _jsx(Input, { variant: "standard", label: "R\u00E9sum\u00E9", value: formFields.title, type: "text", placeholder: "R\u00E9sum\u00E9", onChange: (e) => setFormFields((prev) => ({ ...prev, title: e.target.value })), crossOrigin: "", required: true }) }), errors.title && (_jsx("p", { className: "text-sm text-red-500 mt-1", children: errors.title })), _jsx("div", { className: "px-2", children: _jsxs("div", { className: "flex flex-col md:flex-row md:-mx-2 space-y-4 md:space-y-0", children: [_jsx("div", { className: "w-full md:w-1/2 md:px-2", children: _jsx("div", { className: "", children: _jsxs("div", { children: [_jsx(PrioritySelector, { initialValue: priority ?? task?.priority, onSelect: priorityOption => setPriority(priorityOption) }), _jsx(ProjectSelector, { initialValue: project ?? task?.project, onSelect: projectOption => setProject(projectOption) }), _jsx(ClientSelector, { initialValue: client ?? task?.client, onSelect: clientOption => setClient(clientOption) })] }) }) }), _jsx("div", { className: "w-full md:w-1/2 md:px-2", children: _jsx(AssigneesSection, { onSelect: users => setTaskAssignees(users), initialValue: task?.assignees }) })] }) }), _jsxs("div", { className: "px-2", children: [_jsxs("div", { className: "flex flex-col md:flex-row md:-mx-2 space-y-4 md:space-y-0", children: [_jsx("div", { className: "w-full md:w-1/2 md:px-2", children: _jsx("div", { className: "flex items-center pt-4", children: _jsx(Input, { variant: "standard", label: "R\u00E9f. fonci\u00E8re", value: formFields.ref_fonciere, type: "text", placeholder: "R\u00E9f\u00E9rence fonci\u00E8re", onChange: (e) => setFormFields((prev) => ({ ...prev, ref_fonciere: e.target.value })), crossOrigin: "" }) }) }), _jsx("div", { className: "w-full md:w-1/2 md:px-2", children: _jsx(CitySelector, { initialValue: taskCity, onSelect: cityOption => setTaskCity(cityOption) }) })] }), _jsx("div", { className: "flex flex-col md:flex-row md:-mx-2 space-y-4 md:space-y-0 mt-4", children: _jsx("div", { className: "w-full md:w-1/2 md:px-2", children: _jsx(Input, { variant: "standard", label: "\u00C9ch\u00E9ance", type: "date", value: formFields.due_date, onChange: (e) => setFormFields((prev) => ({ ...prev, due_date: e.target.value })), className: "w-full", crossOrigin: "" }) }) })] }), _jsx("div", { className: "mt-4", children: _jsx(Textarea, { variant: "standard", label: "Adresse", value: formFields.adresse, onChange: (e) => setFormFields((prev) => ({ ...prev, adresse: e.target.value })), className: "w-full" }) }), _jsx("div", { className: "mt-4", children: _jsx(Textarea, { variant: "standard", label: "Description", value: formFields.description, onChange: (e) => setFormFields((prev) => ({ ...prev, description: e.target.value })), className: "w-full" }) }), _jsx(MissionsSection, { onSelect: missions => setTaskMissions(missions), initialValue: task?.taskMissions }), _jsx(TravelsSection, { onSelect: travel => setTaskTravels(travel), initialValue: task?.travels }), _jsx(ChargesSection, { onSelect: charges => setTaskCharges(charges), initialValue: task?.taskCharges }), _jsx(FormActions, { onSave: handleSave, onCancel: onCancel })] }) })] }));
};
export default TaskForm;
